import { Trans, useTranslation } from 'react-i18next';
import FullBlockingModal from '../common/FullBlockingModal';
import { useState } from 'react';
import { useAllianceStore } from '../../stores/allianceStore';
import { UserAlliance } from '../../types/alliance';
import FreelancerProfileModalContent from '../freelancers/FreelancerProfileModalContent';
import { Button } from '../common/Button';
import { useIsFutureEnabled } from '../../hooks/useIsFeatureEnabled';
import { toast } from 'react-toastify';
import { mapUserAllianceToSearchResult } from '../../utils/freelancerUtils';
import ProfileBioText from '../common/ProfileBioText';

interface AllianceAcceptContentProps {
  alliance: UserAlliance;
  onClose: () => void;
  onDecline: () => void;
}

const AllianceAcceptContent = ({ alliance, onClose, onDecline }: AllianceAcceptContentProps) => {
  const { t } = useTranslation(['alliances', 'freelancers', 'common']);
  const { acceptAllianceRequest } = useAllianceStore();
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const isCreditFeatureEnabled = useIsFutureEnabled('credit');

  const messageTitle = alliance.request_message
    ? t('allianceAcceptRequestMessageLabel', {
        ns: 'alliances',
        defaultValue: 'Message de {{ requesterName}}',
        requesterName: alliance.user.first_name,
      })
    : t('about', { ns: 'freelancers', defaultValue: 'À propos' });
  const message = alliance.request_message || alliance.user.bio;

  const primaryAllianceButtonLabel = isCreditFeatureEnabled
    ? t('allianceAcceptButtonLabel', {
        ns: 'alliances',
        defaultValue: 'Utiliser 1 jeton et accepter',
      })
    : t('common:form_actions.accept');

  const handleAcceptAllianceRequest = async (allianceId: string) => {
    setAccepting(true);
    try {
      await acceptAllianceRequest(allianceId);
      setAccepted(true);
    } catch (error) {
      console.error(error);
      toast.error(
        t('error.generic', {
          ns: 'common',
          defaultValue: "Une erreur est survenue lors de l'acceptation de l'alliance",
        })
      );
    } finally {
      setAccepting(false);
    }
  };

  if (accepted) {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl font-bold text-secondary">
          {t('allianceAcceptedTitle', { ns: 'alliances', defaultValue: 'Demande acceptée' })}
        </h2>

        <p className="text-secondary mb-4">
          <Trans
            i18nKey={'allianceAcceptedMessage'}
            ns={'alliances'}
            values={{ requesterName: alliance.user.first_name }}
            components={{ bold: <span className="font-bold" /> }}
          />
        </p>

        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button className="w-full md:w-auto md:px-8" variant="primary" onClick={onClose}>
            {t('backToAlliances', { ns: 'alliances', defaultValue: 'Retourner à mes Alliances' })}
          </Button>
          <Button
            className="w-full md:w-auto md:px-8"
            variant="transparent-with-border"
            href={'/quotes'}
          >
            {t('manageQuotes', { ns: 'alliances', defaultValue: 'Gérer mes soumissions' })}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-col">
        <h2 className="text-2xl font-bold text-secondary pt-6 md:pt-0 mb-4">
          {t('allianceAcceptTile', {
            ns: 'alliances',
            defaultValue: "Accepter une demande d'Alliance",
          })}
        </h2>
        <span className="text-sm text-secondary leading-tight md:mb-4">
          {t('allianceAcceptSubtitle', { ns: 'alliances', defaultValue: 'De la part de' })}
        </span>
        <FreelancerProfileModalContent
          freelancer={mapUserAllianceToSearchResult(alliance)}
          className="mb-4"
        />
        <hr className="border-[#E1E9F0] mb-4" />
        {message && (
          <div className="mb-8">
            <div className="font-bold text-lg mb-1 text-secondary">{messageTitle}</div>
            <div className="text-secondary">
              {alliance.request_message ? (
                alliance.request_message
              ) : (
                <ProfileBioText
                  bio={alliance.user.bio}
                  profileId={alliance.user.id}
                  skipRedaction={true}
                />
              )}
            </div>
          </div>
        )}

        <Button
          className="hidden w-full mb-4 md:flex md:w-auto md:px-8 md:self-end"
          variant="transparent-with-border"
          href={`/freelancers/${alliance.user.id}`}
          onClick={onClose}
        >
          {t('viewFullProfile', { ns: 'freelancers', defaultValue: 'Voir le profil complet' })}
        </Button>

        <hr className="hidden border-[#E1E9F0] mb-4 md:block" />

        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button
            className="w-full md:w-auto md:px-8"
            variant="primary"
            onClick={() => handleAcceptAllianceRequest(alliance.alliance_id)}
            disabled={accepting}
          >
            {accepting ? t('common:form_actions.accepting') : primaryAllianceButtonLabel}
          </Button>

          <Button
            className="w-full md:w-auto md:px-8"
            variant="transparent-with-border"
            onClick={onDecline}
          >
            {t('allianceDeclineButtonLabel', {
              ns: 'alliances',
              defaultValue: 'Refuser la demande',
            })}
          </Button>

          <Button
            className="w-full mb-4 md:hidden md:w-auto md:px-8 md:self-end"
            variant="transparent-with-border"
            href={`/freelancers/${alliance.user.id}`}
            onClick={onClose}
          >
            {t('viewFullProfile', { ns: 'freelancers', defaultValue: 'Voir le profil complet' })}
          </Button>

          <button
            className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
            onClick={onClose}
          >
            {t('closeFreelancerProfileModal', {
              ns: 'alliances',
              defaultValue: 'Fermer et retourner aux Alliances',
            })}
          </button>
        </div>
      </div>
    </>
  );
};

interface AllianceDeclineContentProps {
  alliance: UserAlliance;
  onClose: () => void;
}

const AllianceDeclineContent = ({ alliance, onClose }: AllianceDeclineContentProps) => {
  const { t } = useTranslation('alliances');
  const { declineAllianceRequest } = useAllianceStore();

  const [declining, setDeclining] = useState(false);
  const [declineSuccess, setDeclineSuccess] = useState(false);
  const [allianceDeclineError, setAllianceDeclineError] = useState<string | null>(null);
  const [allianceDeclineMessage, setAllianceDeclineMessage] = useState('');

  const allianceDeclineMessagePlaceholderKey = declineSuccess
    ? 'allianceDeclinedMessage'
    : 'allianceDeclineMessageDescription';

  const allianceDeclineTitleKey = declineSuccess ? 'allianceDeclinedTitle' : 'allianceDeclineTile';

  const handleDeclineAllianceRequest = async (allianceId: string, rejectReason?: string) => {
    setDeclining(true);
    setAllianceDeclineError(null);
    try {
      await declineAllianceRequest(allianceId, rejectReason);
      setDeclineSuccess(true);
    } catch (error) {
      console.error(error);
      setAllianceDeclineError(t('allianceDeclineError', { ns: 'alliances' }));
    } finally {
      setDeclining(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 pt-6 md:pt-0">
      <h2 className="text-2xl font-bold text-secondary">
        {t(allianceDeclineTitleKey, {
          ns: 'alliances',
        })}
      </h2>

      <p className="text-secondary">
        <Trans
          i18nKey={allianceDeclineMessagePlaceholderKey}
          ns={'alliances'}
          values={{ requesterName: alliance.user.first_name }}
          components={{ bold: <span className="font-bold" /> }}
        />
      </p>

      {!declineSuccess && (
        <div className="flex flex-col gap-1">
          <label className="text-secondary" htmlFor="allianceDeclineMessage">
            <Trans
              i18nKey={'allianceMessageForLabel'}
              ns={'alliances'}
              values={{ freelancerName: alliance.user.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          </label>
          <textarea
            name="allianceDeclineMessage"
            value={allianceDeclineMessage}
            onChange={e => setAllianceDeclineMessage(e.target.value)}
            placeholder={t('allianceDeclineMessagePlaceholder', { ns: 'alliances' })}
            className="w-full h-[8.3125rem] border-0 px-3 py-2 font-ambit text-[0.8125rem] not-italic font-normal leading-[1.25rem] bg-[#E1E9F0] md:h-[5.3125rem]"
            maxLength={1000}
          />
          {allianceDeclineError && (
            <div className="text-[#F04438] text-[0.9375rem] font-ambit mt-1">
              {allianceDeclineError}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
        {declineSuccess ? (
          <Button variant="primary" onClick={onClose} className="w-full md:w-auto md:px-8">
            {t('backToAlliances', { ns: 'alliances', defaultValue: 'Retourner à mes Alliances' })}
          </Button>
        ) : (
          <>
            <Button
              className="w-full md:w-auto md:px-8"
              variant="primary"
              onClick={() =>
                handleDeclineAllianceRequest(alliance.alliance_id, allianceDeclineMessage)
              }
            >
              {declining
                ? t('common:form_actions.declining')
                : t('allianceDeclineButtonLabel', {
                    ns: 'alliances',
                    defaultValue: 'Refuser la demande',
                  })}
            </Button>

            <button
              className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
              onClick={onClose}
            >
              {t('closeFreelancerProfileModal', {
                ns: 'alliances',
                defaultValue: 'Fermer et retourner aux Alliances',
              })}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

interface AllianceRespondModalProps {
  alliance: UserAlliance;
  onClose: () => void;
}

export function AllianceRespondModal({ alliance, onClose }: AllianceRespondModalProps) {
  const { t } = useTranslation('alliances');
  const [isDeclineAlliance, setIsDeclineAlliance] = useState(false);

  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>

      {isDeclineAlliance ? (
        <AllianceDeclineContent alliance={alliance} onClose={onClose} />
      ) : (
        <AllianceAcceptContent
          alliance={alliance}
          onClose={onClose}
          onDecline={() => setIsDeclineAlliance(true)}
        />
      )}
    </FullBlockingModal>
  );
}
